import { useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Minus, Plus, Upload } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";
import { Reveal } from "../components/Reveal";

const COLOR_HEX = { Black: "#111", White: "#f2f2f2", Bone: "#d8d0bd", Slate: "#5b6470", Ash: "#9aa0a6", Charcoal: "#2b2b2e", Forest: "#1e3d2f", Sand: "#cbb894", Olive: "#4a5240", Tan: "#b58e5a", Navy: "#1b2a4a", Stone: "#8f8a80" };

export default function ProductDetail() {
  const { id } = useParams();
  const { addItem } = useCart();
  const [product, setProduct] = useState(null);
  const [size, setSize] = useState(null);
  const [color, setColor] = useState(null);
  const [qty, setQty] = useState(1);
  const [method, setMethod] = useState("print");
  const [placement, setPlacement] = useState("front");
  const [customText, setCustomText] = useState("");
  const [artwork, setArtwork] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [library, setLibrary] = useState([]);
  const [notifyEmail, setNotifyEmail] = useState("");
  const [notifyDone, setNotifyDone] = useState(false);
  const fileRef = useRef(null);
  const { user } = useAuth();

  useEffect(() => {
    if (user) {
      api.get("/artwork/mine").then(({ data }) => setLibrary(data)).catch(() => {});
      setNotifyEmail((e) => e || user.email || "");
    }
  }, [user]);

  const requestNotify = async () => {
    try {
      await api.post("/stock-alerts", { product_id: product.id, size, email: notifyEmail.trim() });
      setNotifyDone(true);
      toast.success("We'll email you when it's back");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  useEffect(() => {
    setArtwork(null);
    setCustomText("");
    setNotifyDone(false);
    api.get(`/products/${id}`).then(({ data }) => {
      setProduct(data);
      setSize(data.sizes?.[0] || "OS");
      setColor(data.colors?.[0] || "Black");
    }).catch(() => setProduct(false));
  }, [id]);

  const handleArtwork = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/uploads/artwork", fd);
      setArtwork({ path: data.path, preview: URL.createObjectURL(file) });
      setLibrary((l) => [{ path: data.path, original_filename: file.name }, ...l]);
      toast.success("Artwork attached");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUploading(false);
    }
  };

  const unitPrice = useMemo(() => {
    if (!product) return 0;
    let p = product.price;
    if (product.customizable) {
      if (method === "embroidery") p += 8;
      if (placement === "back") p += 4;
    }
    return p;
  }, [product, method, placement]);

  if (product === null) return <div className="pt-40 text-center font-mono text-xs uppercase tracking-widest text-zinc-500" data-testid="product-loading">Loading garment…</div>;
  if (product === false) return <div className="pt-40 text-center font-mono text-xs uppercase tracking-widest text-red-500" data-testid="product-not-found">Product not found.</div>;

  const stockForSize = product.stock?.[size] ?? 0;

  const handleAdd = () => {
    if (stockForSize <= 0) { toast.error("That size is sold out."); return; }
    addItem({
      product_id: product.id, name: product.name, image: product.image,
      size, color, qty, unit_price: unitPrice,
      customization: product.customizable ? { method, placement, text: customText.trim(), artwork_path: artwork?.path || null } : null,
    });
    toast.success(`${product.name} added to cart`);
  };

  return (
    <div className="max-w-7xl mx-auto px-4 md:px-12 pt-28 pb-10" data-testid="product-detail-page">
      <Link to="/shop" data-testid="back-to-shop-link" className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-widest text-zinc-500 hover:text-kinetic transition-colors mb-8">
        <ArrowLeft size={14} /> Back to shop
      </Link>
      <div className="grid lg:grid-cols-2 gap-8 lg:gap-14">
        <motion.div
          initial={{ clipPath: "inset(0 0 100% 0)" }} animate={{ clipPath: "inset(0 0 0% 0)" }}
          transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
          className="lg:sticky lg:top-24 self-start"
        >
          <div className="relative clipped-frame border border-white/10">
            <img src={product.image} alt={product.name} className="w-full aspect-[4/5] object-cover" data-testid="product-image" />
            <span className="absolute bottom-4 left-4 font-mono text-[10px] uppercase tracking-[0.2em] bg-black/70 backdrop-blur px-3 py-2 text-zinc-300">
              SKU · {product.id.slice(0, 8).toUpperCase()}
            </span>
          </div>
        </motion.div>

        <Reveal>
          <p className="eyebrow mb-3">{product.category}</p>
          <h1 className="font-display text-4xl md:text-5xl uppercase tracking-tight" data-testid="product-name">{product.name}</h1>
          <div className="mt-4 flex items-baseline gap-3 font-mono">
            <span className="text-2xl text-white" data-testid="product-price">${unitPrice.toFixed(2)}</span>
            {product.compare_at && <span className="text-zinc-600 line-through">${product.compare_at.toFixed(2)}</span>}
          </div>
          <p className="mt-6 text-zinc-400 leading-relaxed max-w-lg">{product.description}</p>

          <div className="mt-10 space-y-8">
            <div>
              <p className="font-mono text-xs uppercase tracking-widest text-zinc-500 mb-3">Size — <span className={stockForSize <= 5 ? "text-kinetic" : "text-zinc-300"}>{stockForSize <= 0 ? "Sold out" : stockForSize <= 5 ? `Only ${stockForSize} left` : "In stock"}</span></p>
              <div className="flex flex-wrap gap-2" data-testid="size-selector">
                {product.sizes.map((s) => (
                  <button
                    key={s}
                    data-testid={`size-${s}`}
                    onClick={() => { setSize(s); setNotifyDone(false); }}
                    className={`min-w-12 px-3 py-2.5 border font-mono text-xs uppercase transition-colors active:scale-95 ${
                      size === s ? "bg-kinetic border-kinetic text-white" : "border-white/15 text-zinc-300 hover:border-kinetic"
                    }`}
                  >
                    {s}
                  </button>
                ))}
              </div>
              {stockForSize <= 0 && (
                <div className="mt-4 border border-kinetic/40 bg-card p-4" data-testid="notify-panel">
                  {notifyDone ? (
                    <p className="font-mono text-xs uppercase tracking-widest text-emerald-500" data-testid="notify-success">
                      You're on the list — we'll email you when size {size} is back.
                    </p>
                  ) : (
                    <>
                      <p className="font-mono text-xs uppercase tracking-widest text-zinc-400 mb-3">Size {size} is sold out. Get notified when it's back.</p>
                      <div className="flex gap-2">
                        <input data-testid="notify-email-input" type="email" required value={notifyEmail} onChange={(e) => setNotifyEmail(e.target.value)} placeholder="YOUR EMAIL" className="input-dark font-mono uppercase text-xs tracking-widest !py-2.5" />
                        <button type="button" data-testid="notify-submit-button" onClick={requestNotify} className="btn-kinetic !py-2.5 !px-5">Notify me</button>
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>

            <div>
              <p className="font-mono text-xs uppercase tracking-widest text-zinc-500 mb-3">Color — <span className="text-zinc-300">{color}</span></p>
              <div className="flex gap-3" data-testid="color-selector">
                {product.colors.map((c) => (
                  <button
                    key={c}
                    data-testid={`color-${c.toLowerCase()}`}
                    onClick={() => setColor(c)}
                    title={c}
                    className={`w-9 h-9 border-2 transition-colors active:scale-95 ${color === c ? "border-kinetic animate-ping-swatch" : "border-white/20 hover:border-white/50"}`}
                    style={{ backgroundColor: COLOR_HEX[c] || "#333" }}
                  />
                ))}
              </div>
            </div>

            {product.customizable && (
              <div className="border border-kinetic/40 bg-card p-5 space-y-6" data-testid="customizer-panel">
                <p className="font-mono text-xs uppercase tracking-[0.25em] text-kinetic">Custom Studio</p>
                <div>
                  <p className="font-mono text-xs uppercase tracking-widest text-zinc-500 mb-3">Method</p>
                  <div className="grid grid-cols-2 gap-2">
                    <button data-testid="method-print" onClick={() => setMethod("print")} className={`py-3 border font-mono text-xs uppercase tracking-widest transition-colors active:scale-95 ${method === "print" ? "bg-kinetic border-kinetic text-white" : "border-white/15 text-zinc-300 hover:border-kinetic"}`}>Screen Print</button>
                    <button data-testid="method-embroidery" onClick={() => setMethod("embroidery")} className={`py-3 border font-mono text-xs uppercase tracking-widest transition-colors active:scale-95 ${method === "embroidery" ? "bg-kinetic border-kinetic text-white" : "border-white/15 text-zinc-300 hover:border-kinetic"}`}>Embroidery +$8</button>
                  </div>
                </div>
                <div>
                  <p className="font-mono text-xs uppercase tracking-widest text-zinc-500 mb-3">Placement</p>
                  <div className="grid grid-cols-2 gap-2">
                    <button data-testid="placement-front" onClick={() => setPlacement("front")} className={`py-3 border font-mono text-xs uppercase tracking-widest transition-colors active:scale-95 ${placement === "front" ? "bg-kinetic border-kinetic text-white" : "border-white/15 text-zinc-300 hover:border-kinetic"}`}>Front</button>
                    <button data-testid="placement-back" onClick={() => setPlacement("back")} className={`py-3 border font-mono text-xs uppercase tracking-widest transition-colors active:scale-95 ${placement === "back" ? "bg-kinetic border-kinetic text-white" : "border-white/15 text-zinc-300 hover:border-kinetic"}`}>Back +$4</button>
                  </div>
                </div>
                <div>
                  <p className="font-mono text-xs uppercase tracking-widest text-zinc-500 mb-3">Custom text (optional)</p>
                  <input
                    data-testid="custom-text-input"
                    value={customText}
                    onChange={(e) => setCustomText(e.target.value)}
                    maxLength={24}
                    placeholder="YOUR TEXT HERE"
                    className="input-dark font-mono uppercase tracking-widest"
                  />
                </div>
                <div>
                  <p className="font-mono text-xs uppercase tracking-widest text-zinc-500 mb-3">Artwork / logo (optional)</p>
                  <input ref={fileRef} type="file" accept=".png,.jpg,.jpeg,.webp" className="hidden" onChange={handleArtwork} data-testid="artwork-file-input" />
                  {artwork ? (
                    <div className="flex items-center gap-4">
                      <img src={artwork.preview} alt="Artwork preview" className="w-20 h-20 object-contain border border-kinetic/40 bg-black" data-testid="artwork-preview" />
                      <div>
                        <p className="font-mono text-[11px] text-emerald-500 uppercase tracking-widest">Artwork attached</p>
                        <button type="button" data-testid="artwork-remove-button" onClick={() => setArtwork(null)} className="font-mono text-[11px] text-zinc-500 hover:text-red-500 uppercase tracking-widest mt-1 transition-colors">Remove</button>
                      </div>
                    </div>
                  ) : (
                    <button type="button" data-testid="artwork-upload-button" onClick={() => fileRef.current?.click()} disabled={uploading} className="btn-ghost w-full !py-3">
                      <Upload size={14} /> {uploading ? "Uploading…" : "Upload artwork"}
                    </button>
                  )}
                  <p className="font-mono text-[10px] text-zinc-600 mt-2 uppercase tracking-widest">PNG, JPG or WEBP · max 10MB · attaches to this order</p>
                  {user && library.length > 0 && (
                    <div className="mt-4">
                      <p className="font-mono text-[10px] uppercase tracking-widest text-zinc-600 mb-2">Or pick from your library</p>
                      <div className="flex flex-wrap gap-2" data-testid="artwork-library">
                        {library.map((a) => (
                          <button
                            key={a.path}
                            type="button"
                            data-testid={`artwork-lib-${a.path.split("/").pop()}`}
                            onClick={() => setArtwork({ path: a.path, preview: `${process.env.REACT_APP_BACKEND_URL}/api/files/${a.path}` })}
                            className={`w-14 h-14 border bg-black p-1 transition-colors active:scale-95 ${artwork?.path === a.path ? "border-kinetic" : "border-white/15 hover:border-kinetic/60"}`}
                          >
                            <img src={`${process.env.REACT_APP_BACKEND_URL}/api/files/${a.path}`} alt={a.original_filename || "Saved artwork"} className="w-full h-full object-contain" />
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            <div className="flex items-center gap-4">
              <div className="flex items-center border border-white/15">
                <button data-testid="qty-minus" onClick={() => setQty(Math.max(1, qty - 1))} className="p-3 text-zinc-400 hover:text-kinetic"><Minus size={14} /></button>
                <span className="px-4 font-mono" data-testid="qty-value">{qty}</span>
                <button data-testid="qty-plus" onClick={() => setQty(qty + 1)} className="p-3 text-zinc-400 hover:text-kinetic"><Plus size={14} /></button>
              </div>
              <button data-testid="add-to-cart-button" onClick={handleAdd} disabled={stockForSize <= 0} className="btn-kinetic flex-1 disabled:opacity-40 disabled:pointer-events-none">
                Add to cart — ${(unitPrice * qty).toFixed(2)}
              </button>
            </div>
          </div>
        </Reveal>
      </div>
    </div>
  );
}
